import { SpaceBetween, FormField, Input, RadioGroup } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { EventInput } from '@amzn/red-velvet-api';

export interface AciGrantComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
}

const GRANT_ID_INPUT_MAX_CHAR = 30;

export function AciGrantComponent(props: AciGrantComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const { currentFields, updateFieldHandler, errors, isReadOnly } = props;

  const validateGrantId = (providedGrantId: string): boolean => {
    const grantIdPattern = /^\d{4}VG-(AMER|APAC|EMEA)-\d{6}$/;
    return grantIdPattern.test(providedGrantId) || providedGrantId === '';
  };

  const [aciGrant, setAciGrant] = useState(currentFields.usedAciGrant);
  const [grantId, setGrantId] = useState(currentFields.grantId ?? '');
  const [grantIdValid, setGrantIdValid] = useState(() =>
    validateGrantId(currentFields.grantId ?? '')
  );

  const renderGrantInfo = () => {
    if (aciGrant === true) {
      return (
        <FormField
          label={t('eventDetailsStep.grantId')}
          description={t('eventDetailsStep.grantIdSubText')}
          {...(!grantIdValid ? { warningText: t('eventDetailsStep.grantIdWarning') } : {})}
          errorText={
            errors?.grantId ??
            (grantId?.length >= GRANT_ID_INPUT_MAX_CHAR
              ? t('error.inputTooLong', { maxLength: GRANT_ID_INPUT_MAX_CHAR })
              : '')
          }
        >
          <Input
            placeholder={t('eventDetailsStep.grantIdPlaceholder')}
            onChange={(event) => {
              const providedGrantId = event.detail.value;
              const isValid = validateGrantId(providedGrantId);
              setGrantIdValid(isValid);
              setGrantId(providedGrantId);
              updateFieldHandler({
                ...currentFields,
                grantId: providedGrantId,
              });
            }}
            value={grantId}
            readOnly={isReadOnly}
          />
        </FormField>
      );
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <FormField
        label={t('eventDetailsStep.aciGrant')}
        description={t('eventDetailsStep.aciGrantSubText')}
        errorText={errors?.usedAciGrant}
      >
        <RadioGroup
          onChange={(event) => {
            setAciGrant(event.detail.value === 'true');
            // Resetting the grant id value each time aci grant change
            setGrantId('');
            setGrantIdValid(true);
            updateFieldHandler({
              ...currentFields,
              usedAciGrant: event.detail.value === 'true',
              grantId: '',
            });
          }}
          value={aciGrant?.toString() ?? ''}
          items={[
            { value: 'true', label: t('misc.yes') },
            { value: 'false', label: t('misc.no') },
          ]}
          readOnly={isReadOnly}
        />
      </FormField>
      {renderGrantInfo()}
    </SpaceBetween>
  );
}
